import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core'
import { FormControl, FormGroup } from '@angular/forms'
import {
  NoteContentValidator,
  NotesValidator,
  notesValidationMessages,
} from '@helios/validators'
import { Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import {
  BulkBreakGroup,
  isBreakNoted,
} from '../../model/bulk-break-group.model'
import {
  actionCodeOptions,
  descriptionCodeOptions,
  notesRequiredActionCodes,
} from '../../validators/break-code-validation'
import {
  GroupSelection,
  GroupValidation,
  KeepMode,
  prefillGroupSelection,
  validateGroup,
  writeTargets,
} from '../../validators/bulk-break-validation'
import { BREAK_DIALOG_SHARED_STYLES } from '../break-dialog-shared.styles'

@Component({
  selector: 'hs-bulk-break-group',
  templateUrl: './bulk-break-group.component.html',
  styles: [
    BREAK_DIALOG_SHARED_STYLES,
    // Light palette matching the mockup + the existing modals (which rely on
    // nf-*/bootstrap and only add small accent rules). Do NOT introduce a dark
    // theme — the app's dialogs are light.
    `
      .group-header {
        display: flex;
        align-items: center;
        gap: 8px;
        cursor: pointer;
        padding: 8px 10px;
        background: #f0f4f9;
        border: 1px solid #d0d7e2;
        border-radius: 4px;
      }
      .group-header:hover {
        background: #e6edf6;
      }
      .break-type-badge {
        font-weight: 700;
        font-size: 14px;
        color: #1a5ea8;
        background: #dde8f5;
        border-radius: 3px;
        padding: 2px 7px;
      }
      .coded-indicator {
        font-size: 11px;
        color: #6b7a8d;
      }
      .pill {
        margin-left: auto;
        font-size: 11px;
        font-weight: 600;
        border-radius: 10px;
        padding: 2px 8px;
      }
      .pill-complete {
        background: #d4edda;
        color: #155724;
      }
      .pill-incomplete {
        background: #f0f0f0;
        color: #777;
      }
      .save-badge {
        font-size: 11px;
        font-weight: 600;
        border-radius: 10px;
        padding: 2px 8px;
      }
      .save-badge-saving {
        background: #e2e8f0;
        color: #475569;
      }
      .save-badge-saved {
        background: #d4edda;
        color: #155724;
      }
      .save-badge-failed {
        background: #f8d7da;
        color: #842029;
      }
      .group-body {
        padding: 10px 12px;
        border: 1px solid #d0d7e2;
        border-top: none;
      }
      /* Drop the trailing control's bottom margin so there's no dead space
         under the note box. */
      .group-body > :last-child {
        margin-bottom: 0;
      }
      /* Middle font tier: the code dropdowns and note box sit between the
         break-type badge (14px) and the status/info text (11px). */
      .group-body textarea.form-control {
        font-size: 12px;
      }
      .group-body ::ng-deep .ng-select,
      .group-body ::ng-deep .ng-dropdown-panel .ng-option {
        font-size: 12px;
      }
      .existing-codes-info {
        font-size: 11px;
        color: #6b7a8d;
        margin-bottom: 8px;
      }
      .group-blocker {
        font-size: 12px;
        color: #8c3a1e;
        background: #fdf3f0;
        border: 1px solid #e8c5b8;
        border-radius: 3px;
        padding: 8px;
      }
    `,
  ],
})
export class BulkBreakGroupComponent implements OnInit, OnChanges, OnDestroy {
  @Input() group!: BulkBreakGroup
  @Input() keepMode!: KeepMode
  @Input() mode!: 'hold' | 'accept'
  @Input() saveState: 'idle' | 'saving' | 'saved' | 'failed' = 'idle'
  @Input() expanded = false
  // False under Append, where the note box is text to add. Read live by the
  // placeholder, but only at first render by the prefill; later toggles move the
  // note in the dialog (swapPrefilledNotes).
  @Input() prefillNote = true
  @Output() selectionChange = new EventEmitter<GroupSelection>()
  @Output() toggle = new EventEmitter<void>()

  actionCodes: string[] = []
  descriptionCodes: string[] = []
  notesRequiredActionCodeSet = new Set<string>()

  // Untyped FormControls — this repo is Angular 12 (typed reactive forms and
  // the `nonNullable` option arrived in Angular 14). Matches existing modals.
  readonly form = new FormGroup({
    action: new FormControl(null),
    description: new FormControl(null),
    note: new FormControl('', [NotesValidator(true), NoteContentValidator()]),
  })

  readonly noteValidationMessages = notesValidationMessages

  private readonly destroy$ = new Subject<void>()

  ngOnInit(): void {
    this.form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.recomputeNoteValidators()
      this.selectionChange.emit(this.currentSelection)
    })
    this.recomputeNoteValidators()
    // Announce the initial selection once via an explicit emit (NOT startWith —
    // RxJS 6's single-value startWith overload is @deprecated; this mirrors the
    // note-editor convention from PVAL-25661).
    this.selectionChange.emit(this.currentSelection)
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['group'] && this.group) {
      const av = this.group.availability
      this.actionCodes = actionCodeOptions(av)
      this.descriptionCodes = descriptionCodeOptions(av)
      this.notesRequiredActionCodeSet = notesRequiredActionCodes(av)
      if (changes['group'].firstChange) {
        this.prefillFromBreaks()
      }
    }
    // keepMode/group changes can flip whether the note is required.
    if (changes['group'] || changes['keepMode']) {
      this.recomputeNoteValidators()
    }
  }

  /**
   * Keep the note control's allowEmpty in sync with whether a note is required for
   * this group, so an empty required note red-borders while an empty optional note
   * does not
   */
  private recomputeNoteValidators(): void {
    const allowEmpty = !this.validation.noteRequired
    this.form.controls.note.setValidators([
      NotesValidator(allowEmpty),
      NoteContentValidator(),
    ])
    this.form.controls.note.updateValueAndValidity({ emitEvent: false })
  }

  /**
   * Pre-select the breaks' existing codes and, when `prefillNote`, their shared note,
   * mirroring the single Hold/Accept dialog. The dialog seeds `selections` from the
   * same helper, so the parent already holds these. emitEvent:false — the ngOnInit
   * emit carries them up.
   */
  private prefillFromBreaks(): void {
    const pre = prefillGroupSelection(this.group, this.prefillNote)
    if (pre.actionCode) {
      this.form.controls.action.setValue(pre.actionCode, { emitEvent: false })
    }
    if (pre.descriptionCode) {
      this.form.controls.description.setValue(pre.descriptionCode, {
        emitEvent: false,
      })
    }
    if (pre.note) {
      this.form.controls.note.setValue(pre.note, { emitEvent: false })
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  get currentSelection(): GroupSelection {
    return {
      actionCode: this.form.controls.action.value ?? null,
      descriptionCode: this.form.controls.description.value ?? null,
      note: this.form.controls.note.value ?? '',
    }
  }

  get validation(): GroupValidation {
    return validateGroup(this.group, this.currentSelection, this.keepMode)
  }

  get notePlaceholder(): string {
    if (this.validation.noteRequired) {
      return 'A note is required for the selected code(s).'
    }
    return !this.prefillNote && this.group.breaks.some(isBreakNoted)
      ? 'Add to existing note'
      : 'Optional note'
  }

  get isReady(): boolean {
    return this.group.availability.status === 'ready'
  }

  get isMixed(): boolean {
    return this.group.availability.status === 'mixedCodeSets'
  }

  get codedIndicator(): string {
    if (!this.isReady && !this.isMixed) {
      return 'Codes pending'
    }
    const noCodesAvailable =
      this.isReady &&
      this.actionCodes.length === 0 &&
      this.descriptionCodes.length === 0
    // A break type with no break codes is note-driven: a note is the requirement,
    // so label it "Notes required"
    if (this.group.existingCount === 0 && noCodesAvailable) {
      return 'Notes required'
    }
    return `${this.group.existingCount}/${this.group.totalCount} have break codes`
  }

  /**
   * Gate the Action select: hidden when Keep would preserve every break's action.
   * Per field, so a group whose breaks all carry an action code but no description
   * still gets its Description select.
   */
  get hasActionWriteTarget(): boolean {
    return writeTargets(this.group, this.keepMode, 'action').length > 0
  }
  /** Gate the Description select, independently of the Action one. */
  get hasDescriptionWriteTarget(): boolean {
    return writeTargets(this.group, this.keepMode, 'description').length > 0
  }

  /** Either field writable — gates the row that holds both selects. */
  get hasWriteTarget(): boolean {
    return this.hasActionWriteTarget || this.hasDescriptionWriteTarget
  }

  /** Breaks that would receive a code, for the human summary below — the union of
   *  the two fields' targets, since a break may be a target for only one. */
  get writeCount(): number {
    if (this.keepMode === 'override') return this.group.breaks.length
    const targeted = new Set([
      ...writeTargets(this.group, 'keep', 'action').map(b => b.identifier),
      ...writeTargets(this.group, 'keep', 'description').map(b => b.identifier),
    ])
    return targeted.size
  }

  get existingCodesInfo(): string {
    // Say nothing until codes resolve. writeCount is 0 for a group whose codes failed
    // to load and for a mixedCodeSets one, and any sentence about coding would be a
    // guess — the header indicator and the blocker box already describe those.
    if (!this.isReady) return ''
    if (this.keepMode !== 'keep') {
      return `Overriding codes on all ${this.writeCount} break(s).`
    }
    if (this.writeCount > 0) {
      return `Keeping existing codes on ${this.group.existingCount} break(s); applying to ${this.writeCount}.`
    }
    // Nothing left to write: either every break is coded, or the break type configures
    // no code to write. Only the first lets us claim the breaks are coded.
    return this.group.existingCount === this.group.totalCount
      ? `All ${this.group.totalCount} already coded — will be held/accepted, codes kept.`
      : `No break codes available for this break type.`
  }

  onHeaderClick(): void {
    this.toggle.emit()
  }
}
